
import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { parseGanttExcel } from './Importer';

import useTranslation from '../../Hooks/UseTranslation';

import { importGanttFromExcel } from '../../Services/MacroService';

import { Row } from '../DataGrid/DataGrid';
import DragAndDropArea from './DragAnDropArea';

import './TasksImporterV2.scss'

type Props = {
  show: boolean
  projectId: string
  onHide: () => void
  onImported: () => void
}

const TasksImporterV2 = (props: Props) => {

  const { t } = useTranslation()

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { colorCodes } = useSelector((state: any) => state.ProgrammationReducer)

  const [ data, setData ] = useState<any>([])
  const [ importing, setImporting ] = useState<boolean>(false)

  const [ bodyHeight, setBodyHeight ] = useState<number | string>('auto')

  const columns = [
    {
      key: 'id',
      name: t('TASK_IMPORTER:IdColumn'),
      width: 60,
    },
    {
      key: 'name',
      name: t('TASK_IMPORTER:TitleColumn'),
      width: 400
    },
    {
      key: 'start',
      name: t('TASK_IMPORTER:StartsAtColumn'),
      width: 160
    },
    {
      key: 'end',
      name: t('TASK_IMPORTER:EndsAtColumn'),
      width: 160
    },
    {
      key: 'code',
      name: t('TASK_IMPORTER:CodeColumn'),
      width: 160
    },
    {
      key: 'color',
      name: t('TASK_IMPORTER:ColorColumn'),
      width: 160,
      render: (field: any) => {

        return (
          <div style={{ backgroundColor: field }}>
            &nbsp;
          </div>
        )
      }
    }
  ]

  const readFile = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const { data, errors } = parseGanttExcel(buffer, colorCodes);
    setData(data);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  const handleClose = () => {

    setData([])

    props.onHide()
  }

  const handleImport = async () => {

    setImporting(true)

    await importGanttFromExcel(props.projectId, data)

    setImporting(false)

    setData([])

    props.onImported()
  }

  useEffect(() => {

    const getBodyHeight = () => {
      const modalHeight = window.innerHeight - 56
      const modalFooterHeight = 64

      const modalTitleHeight = 40

      const gridHeaderHeight = 31

      return modalHeight - modalFooterHeight - modalTitleHeight - gridHeaderHeight - 65
    }

    const handleResize = () => {
      const bodyHeight = getBodyHeight()

      setBodyHeight(bodyHeight)
    }

    if (!data.length) {

      setBodyHeight('auto')
    } else {

      handleResize()
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      dialogClassName={data.length ? 'modal-tasks-importer' : ''}
      backdrop='static'
      keyboard
      scrollable
    >
      <Modal.Header>
        <Modal.Title>Importar Gantt</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ul-widget__body" style={{
          overflowY: 'auto',
          height: bodyHeight,
          position: 'relative',
        }}>
          {!data.length && (
            <DragAndDropArea onFileSelected={readFile} />
          )}
          {data && data.length > 0 && (
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  {columns.map((col, index) => (
                    <th key={index}>{col.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row: Row, index: number) => (
                  <tr key={index}>
                    {columns.map((col, index) => (
                      <td key={index}>
                        {col.render && col.render(row[col.key])}
                        {!col.render && row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t('TASK_IMPORTER:CancelButton')}
        </Button>
      <Button variant="primary"
        onClick={handleImport}
        disabled={!data.length}
      >
        {!importing && t('TASK_IMPORTER:ImportButton')}
        {importing && t('TASK_IMPORTER:ImportingButton')}
      </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TasksImporterV2;
